import {
  Alert,
  Box,
  Button,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FC, ReactElement, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LayoutSign } from "../Layout/layout-sign/layout-sign";
import { LayoutContainer } from "../Layout/layout.container";
import { useNavigate, useLocation } from "react-router-dom";
import { CognitoAuthService } from "../Services/CognitoAuthService";
import { updateFirstResponderbyId } from "../Services/firstResponderService";
import { sendVerificationCodeMFA } from "../Services/emailService";
import { FirstResponderData } from "../Interfaces/FirstResponder";
import { useCognitoAwsV2 } from "../Hooks/useCognitoAwsv2";
import { CustomButton } from "../components/CustomButton/CustomButton";

const CssTextField = styled(TextField)({
  "& label": {
    fontStyle: "normal",
    fontWeight: "600",
    fontSize: "16px",
    lineHeight: "24px",
    color: "#858C94",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "white",
    borderRadius: "8px",
  },
  "& .MuiOutlinedInput-root": {
    background: "#FFF",
    borderRadius: "8px",
    width: "328px",
    color: "black",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    // color: "#ffffff",
    fontSize: "14px",
    marginLeft: "0px",
    marginTop: "4px",
    border: "1px solid",
    borderRadius: "5px",
    paddingLeft: "4px",
    backgroundColor: "#ffffff",
  },
});

export const ValidationCode: FC<any> = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const firstResponder = location.state as FirstResponderData;
  const cognitoAuthService = new CognitoAuthService();
  const [isSentEmail, setIsSentEmail] = useState(false);
  const { confirmSignUp } = useCognitoAwsV2();

  const [isLoading, setIsLoading] = useState(false);
  const [mfaCode, setMFACode] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    validateOnChange: true, // validate immediately while typing
    validateOnBlur: false,
    initialValues: {
      code: "",
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .required("Code is required")
        .matches(/^\d{6}$/, "Code must be exactly 6 digits"),
    }),
    onSubmit: async (values) => {
      await handleFormSubmit(values.code);
    },
  });

  useEffect(() => {
    setMFACode(firstResponder?.mfaCode || "");
  }, [firstResponder]);

  const handleFormSubmit = async (code: string) => {
    if (isLoading) return;
    setIsLoading(true);

    if (!firstResponder.mfa) {
      try {
        await confirmSignUp(firstResponder.email, code);
        await updateFirstResponderbyId(firstResponder.id, {
          mfaCode: "123",
          mfa: false,
        });
        navigate("/SearchSubscriber", { state: firstResponder.email });
      } catch (error: any) {
        setErrorMessage(error.message);
        setOpen(true);
      }
    } else {
      if (code === mfaCode) {
        navigate("/SearchSubscriber", { state: firstResponder });
      } else {
        setErrorMessage("The code is invalid. Please try again.");
        setOpen(true);
      }
    }
    setIsLoading(false);
  };

  const sendVerificationCode = async () => {
    setIsLoading(true);
    setIsSentEmail(false);
    // TODO: SMS sending
    // await sendPhoneCodeMFA("+573102583518",firstResponder.subCognitoId);

    const customCode = Math.floor(100000 + Math.random() * 900000);
    await updateFirstResponderbyId(firstResponder.id, {
      mfaCode: String(customCode),
    })
      .then(async (res: any) => {
        console.log(res);
        await sendVerificationCodeMFA(firstResponder.email, String(customCode))
          .then(async (res: any) => {
            setIsSentEmail(true); //
          })
          .catch(async (err: any) => {
            setErrorMessage(err.message);
            setOpen(true);
          });
        setMFACode(res.mfaCode);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
        setErrorMessage("The Code was not generated");
        setOpen(true);
      });
  };

  const handleCloseSnackbar = () => {
    setOpen(false);
  };

  return (
    <LayoutSign
      children={
        <LayoutContainer>
          <Typography component="h2" variant="h5">
            Verification Code
          </Typography>
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            noValidate
            sx={{ mt: 3, mb: 0 }}
          >
            <CssTextField
              name="code"
              placeholder="Enter code"
              autoComplete="off"
              value={formik.values.code}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.code)}
              helperText={formik.errors.code}
              inputProps={{ maxLength: 6 }}
            />

            {isSentEmail && (
              <Typography
                fontStyle="normal"
                fontWeight="400"
                fontSize="12px"
                lineHeight="20px"
                margin="9px 40px"
                textAlign="initial"
                color="#FFFFFF"
              >
                A verification code was sent to {firstResponder.email}
              </Typography>
            )}

            {firstResponder.mfa && (
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Button
                    onClick={sendVerificationCode}
                    variant="text"
                    style={{
                      textTransform: "none",
                      height: 8,
                      fontFamily: "Source Sans Pro",
                      fontStyle: "normal",
                      fontWeight: "600",
                      fontSize: "16px",
                      lineHeight: "24px",
                      color: "white",
                    }}
                  >
                    Send via email
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    onClick={sendVerificationCode}
                    variant="text"
                    style={{
                      textTransform: "none",
                      height: 8,
                      fontFamily: "Source Sans Pro",
                      fontStyle: "normal",
                      fontWeight: "600",
                      fontSize: "16px",
                      lineHeight: "24px",
                      color: "white",
                    }}
                  >
                    Resend code
                  </Button>
                </Grid>
              </Grid>
            )}

            <Grid item pt={4}>
              <Alert
                severity="warning"
                icon={false}
                sx={{ fontSize: 10, background: "#fff6d7" }}
              >
                If you do not see the email in a few minutes, check your{" "}
                <b>&quot;Junk Mail&quot;</b> or <b>&quot;Spam&quot;</b> folder
                and add the MyMedHistory&apos;s email to your{" "}
                <b>&quot;Safe Sender List.&quot;</b> If nothing helped, please
                try submitting the form again.
              </Alert>
            </Grid>

            <CustomButton
              text="Submit"
              type="submit"
              disabled={!formik.isValid || !formik.dirty || isLoading}
              isLoading={isLoading}
              sx={{ mt: 5, mb: 1 }}
            />
          </Box>

          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="error"
              sx={{ width: "100%" }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>
        </LayoutContainer>
      }
    />
  );
};

export default ValidationCode;
